import { useCallback } from 'react';

import { NativeSelect } from '@/lib/base/forms/select/select-native';
import { Toggle } from '@/lib/base/forms/toggle';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { AlertFloating } from '@/lib/base/overlays';

import { useHubspotConnection } from '../hooks/useHubspotConnection';
import type { HubspotConnectionSettings } from '../types';

const DIRECTION_OPTIONS = [
  { value: 'bidirectional', label: 'Two-way sync' },
  { value: 'inbound_only', label: 'HubSpot → Ahoy' },
  { value: 'outbound_only', label: 'Ahoy → HubSpot' },
] as const;

interface ConnectionControlsProps {
  orgId: number | string;
}

export function ConnectionControls({ orgId }: ConnectionControlsProps) {
  const { settings, updateSettings } = useHubspotConnection(orgId);

  const handleDirectionChange = useCallback(
    (e: { target: { value: string } }) => {
      updateSettings.mutate({
        default_sync_direction: e.target
          .value as HubspotConnectionSettings['default_sync_direction'],
      });
    },
    [updateSettings]
  );

  const handleDeletesChange = useCallback(
    (isSelected: boolean) => {
      updateSettings.mutate({ propagate_outbound_deletes: isSelected });
    },
    [updateSettings]
  );

  if (settings.isLoading) {
    return <LoadingIndicator size='sm' />;
  }

  if (settings.isError || !settings.data) {
    return (
      <AlertFloating
        title="Couldn't load connection settings"
        description='Please refresh the page or try again later.'
        color='error'
      />
    );
  }

  const direction = settings.data.default_sync_direction;
  const deletes = settings.data.propagate_outbound_deletes;

  return (
    <div className='flex flex-col gap-4'>
      <NativeSelect
        label='Sync direction'
        options={DIRECTION_OPTIONS}
        value={direction}
        onChange={handleDirectionChange}
        disabled={updateSettings.isPending}
      />
      <Toggle
        label='Propagate deletions to HubSpot'
        hint='When a record is deleted in Ahoy, archive it in HubSpot too.'
        isSelected={deletes}
        onChange={handleDeletesChange}
        isDisabled={updateSettings.isPending}
      />
      {updateSettings.isError && (
        <AlertFloating
          title="We couldn't save connection settings"
          description='Your change could not be saved. Please try again.'
          color='error'
        />
      )}
    </div>
  );
}
